import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import Switch from 'components/UI/Switch';
import { ReactComponent as CameraIcon } from 'assets/icons/camera.svg';
import { PHASE4_ERROR_CODES } from 'constants/digitalLoanPhase4';
import { fileToPureBase64 } from '../utils/selfieBase64';
import { readPhase4Error } from '../utils/phase4Flow';

const ENROLLMENT_NOT_FOUND = 'IDMISSION_ENROLLMENT_NOT_FOUND';

function isEnrollmentMissing(result, error) {
  const code = result?.verificationResult
    || result?.code
    || error?.data?.error;
  return code === ENROLLMENT_NOT_FOUND;
}

const BiometricFinalScreen = ({
  literals,
  isSubmitting = false,
  onCaptured,
  onVerifySelfie,
  onBack,
  readSelfie = fileToPureBase64,
}) => {
  const fileInputRef = useRef(null);
  const selfieRef = useRef('');
  const [cameraAuthorized, setCameraAuthorized] = useState(false);
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState('');

  useEffect(() => () => {
    selfieRef.current = '';
  }, []);

  const failAttempt = (text, { keepSelfie = false } = {}) => {
    if (!keepSelfie) selfieRef.current = '';
    setMessage(text);
    setMessageTone('invalid');
  };

  const submitVerifiedSelfie = async (selfie) => {
    await onCaptured({
      passed: true,
      fingerprint: selfie,
    });
    selfieRef.current = '';
  };

  const handleFile = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file || isSubmitting || !cameraAuthorized) return;
    setMessage('');
    let selfie = '';
    try {
      selfie = await readSelfie(file);
    } catch {
      failAttempt(literals.temporaryError);
      return;
    }
    if (!selfie || selfie.startsWith('data:')) {
      failAttempt(literals.temporaryError);
      return;
    }
    selfieRef.current = selfie;
    let verifyResult;
    try {
      verifyResult = await onVerifySelfie(selfie);
    } catch (error) {
      if (isEnrollmentMissing(null, error)) {
        failAttempt(literals.enrollmentMissing);
        return;
      }
      failAttempt(literals.temporaryError);
      return;
    }
    if (verifyResult?.verified !== true) {
      failAttempt(
        isEnrollmentMissing(verifyResult)
          ? literals.enrollmentMissing
          : literals.rejected,
      );
      return;
    }
    try {
      await submitVerifiedSelfie(selfieRef.current);
    } catch (error) {
      const parsed = readPhase4Error(error);
      if (
        parsed.code === PHASE4_ERROR_CODES.BIOMETRIC_FINAL_REQUIRED
        && selfieRef.current
      ) {
        try {
          const retry = await onVerifySelfie(selfieRef.current);
          if (retry?.verified === true) {
            await submitVerifiedSelfie(selfieRef.current);
            return;
          }
        } catch {
          failAttempt(literals.temporaryError);
          return;
        }
      }
      failAttempt(literals.temporaryError);
    }
  };

  return (
    <div className='full-screen-container digital-loan-preoffer'>
      <div className='screen-content digital-loan-preoffer__scroll'>
        <h1 className='digital-loan-phase2__title'>{literals.title}</h1>
        <p className='digital-loan-phase2__body mb-4'>{literals.body}</p>
        <p className='font-bold mb-2'>{literals.recommendationsTitle}</p>
        <ul className='mb-4'>
          {(literals.recommendations || []).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <p className='font-bold mb-1'>{literals.privacyTitle}</p>
        <p className='digital-loan-phase2__body mb-4'>{literals.privacyBody}</p>
        <div className='d-flex align-items-center justify-content-between mb-4'>
          <span className='d-flex align-items-center gap-2'>
            <CameraIcon aria-hidden='true' />
            {literals.cameraAuthorization}
          </span>
          <Switch
            name='cameraAuthorization'
            checked={cameraAuthorized}
            onChange={() => setCameraAuthorized((current) => !current)}
          />
        </div>
        {message && (
          <p
            className='digital-loan-phase2__body mb-3'
            role={messageTone === 'invalid' ? 'alert' : 'status'}
          >
            {message}
          </p>
        )}
      </div>
      <div className='screen-actions'>
        <Button
          text={literals.takePhotos}
          disabled={!cameraAuthorized || isSubmitting}
          loading={isSubmitting}
          onClick={() => fileInputRef.current?.click()}
        />
        <Button text={literals.back} type='secondary' onClick={onBack} />
      </div>
      <input
        ref={fileInputRef}
        className='d-none'
        type='file'
        accept='image/*'
        capture='user'
        onChange={() => { handleFile(); }}
      />
    </div>
  );
};

BiometricFinalScreen.propTypes = {
  literals: PropTypes.object.isRequired,
  isSubmitting: PropTypes.bool,
  onCaptured: PropTypes.func.isRequired,
  onVerifySelfie: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  readSelfie: PropTypes.func,
};

export default BiometricFinalScreen;
